import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert, Tab, Tabs } from 'react-bootstrap';
import adminService from '../services/adminService';
import bestDealService from '../services/bestDealService';
import blogService from '../services/blogService';
import updateService from '../services/updateService';
import oneStopService from '../services/oneStopService';
import SurveyManager from '../components/SurveyManager';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    bio: '',
    phone: '',
    address: '',
    roles: ['USER'],
    enabled: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Duplicate users state
  const [duplicateUsers, setDuplicateUsers] = useState({});
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  // Best Deals state
  const [deals, setDeals] = useState([]);
  const [showDealModal, setShowDealModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [dealFormData, setDealFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    linkUrl: '',
    price: '',
    originalPrice: '',
    discountPercentage: '',
    active: true,
    displayOrder: 0
  });

  // Blog state
  const [blogs, setBlogs] = useState([]);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogFormData, setBlogFormData] = useState({
    title: '',
    summary: '',
    content: '',
    excerpt: '',
    published: false,
    premiumOnly: false
  });

  // Updates state
  const [updates, setUpdates] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [updateFormData, setUpdateFormData] = useState({
    title: '',
    content: '',
    summary: '',
    updateType: 'GENERAL',
    priority: 'MEDIUM',
    published: true,
    publishDate: ''
  });

  // OneStop state
  const [oneStopNodes, setOneStopNodes] = useState([]);
  const [showOneStopModal, setShowOneStopModal] = useState(false);
  const [editingOneStop, setEditingOneStop] = useState(null);
  const [oneStopFormData, setOneStopFormData] = useState({
    title: '',
    description: '',
    icon: '',
    url: '',
    displayOrder: 0,
    active: true,
    expanded: false,
    parentId: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load data with error handling for each service
      const results = await Promise.allSettled([
        adminService.getAllUsers(),
        adminService.getAdminStats(),
        bestDealService.getAllDeals(0, 100),
        blogService.getAllPosts(0, 100),
        updateService.getAllUpdates(0, 100),
        oneStopService.getAllNodes()
      ]);

      // Handle users data
      if (results[0].status === 'fulfilled') {
        setUsers(results[0].value.data.content || results[0].value.data || []);
      } else {
        console.warn('Failed to load users:', results[0].reason);
        setUsers([]);
      }

      // Handle stats data
      if (results[1].status === 'fulfilled') {
        setStats(results[1].value.data || {});
      } else {
        console.warn('Failed to load stats:', results[1].reason);
        setStats({});
      }

      // Handle deals data
      if (results[2].status === 'fulfilled') {
        setDeals(results[2].value.data.content || results[2].value.data || []);
      } else {
        console.warn('Failed to load deals:', results[2].reason);
        setDeals([]);
      }

      // Handle blogs data
      if (results[3].status === 'fulfilled') {
        setBlogs(results[3].value.data.content || results[3].value.data || []);
      } else {
        console.warn('Failed to load blogs:', results[3].reason);
        setBlogs([]);
      }

      // Handle updates data
      if (results[4].status === 'fulfilled') {
        setUpdates(results[4].value.data.content || results[4].value.data || []);
      } else {
        console.warn('Failed to load updates:', results[4].reason);
        setUpdates([]);
      }

      // Handle OneStop nodes data
      if (results[5].status === 'fulfilled') {
        setOneStopNodes(results[5].value.data || []);
      } else {
        console.warn('Failed to load OneStop nodes:', results[5].reason);
        setOneStopNodes([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading admin data:', error);
      setError('Failed to load admin data');
      setLoading(false);
    }
  };

  const loadDuplicateUsers = async () => {
    try {
      setDuplicateLoading(true);
      const response = await adminService.getDuplicateUsers();
      setDuplicateUsers(response.data || {});
    } catch (error) {
      console.error('Error loading duplicate users:', error);
      setError('Failed to load duplicate users');
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleShowModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        bio: user.bio || '',
        phone: user.phone || '',
        address: user.address || '',
        roles: Array.from(user.roles),
        enabled: user.enabled !== false,
        premium: user.premium || false,
        premiumExpiresAt: user.premiumExpiresAt || ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        bio: '',
        phone: '',
        address: '',
        roles: ['USER'],
        enabled: true,
        premium: false,
        premiumExpiresAt: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setError('');
    setSuccess('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'roles') {
      const roles = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({ ...formData, roles });
    } else if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'premiumExpiresAt') {
      // Convert datetime-local to ISO string
      const isoValue = value ? new Date(value).toISOString() : null;
      setFormData({ ...formData, [name]: isoValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      
      if (editingUser) {
        await adminService.updateUser(editingUser.id, formData);
        setSuccess('User updated successfully!');
      } else {
        await adminService.createUser(formData);
        setSuccess('User created successfully!');
      }
      
      loadData();
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (error) {
      // Extract error message from response
      let errorMessage = 'Operation failed. Please try again.';
      
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      console.error('User operation error:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await adminService.deleteUser(userId);
        loadData();
      } catch (error) {
        setError('Failed to delete user');
        console.error('Delete user error:', error);
      }
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await adminService.toggleUserStatus(userId);
      loadData();
    } catch (error) {
      setError('Failed to toggle user status');
      console.error('Toggle status error:', error);
    }
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'ADMIN': return 'danger';
      case 'MODERATOR': return 'warning';
      default: return 'primary';
    }
  };

  // Best Deals functions
  const handleShowDealModal = (deal = null) => {
    if (deal) {
      setEditingDeal(deal);
      setDealFormData({
        title: deal.title || '',
        description: deal.description || '',
        imageUrl: deal.imageUrl || '',
        linkUrl: deal.linkUrl || '',
        price: deal.price || '',
        originalPrice: deal.originalPrice || '',
        discountPercentage: deal.discountPercentage || '',
        active: deal.active !== false,
        displayOrder: deal.displayOrder || 0
      });
    } else {
      setEditingDeal(null);
      setDealFormData({
        title: '',
        description: '',
        imageUrl: '',
        linkUrl: '',
        price: '',
        originalPrice: '',
        discountPercentage: '',
        active: true,
        displayOrder: 0
      });
    }
    setShowDealModal(true);
  };

  const handleCloseDealModal = () => {
    setShowDealModal(false);
    setEditingDeal(null);
    setError('');
    setSuccess('');
  };

  const handleDealChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setDealFormData({ ...dealFormData, [name]: checked });
    } else if (name === 'discountPercentage' || name === 'displayOrder') {
      setDealFormData({ ...dealFormData, [name]: value ? parseInt(value) : '' });
    } else {
      setDealFormData({ ...dealFormData, [name]: value });
    }
  };

  const handleDealSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      
      if (editingDeal) {
        await bestDealService.updateDeal(editingDeal.id, dealFormData);
        setSuccess('Deal updated successfully!');
      } else {
        await bestDealService.createDeal(dealFormData);
        setSuccess('Deal created successfully!');
      }
      
      loadData();
      setTimeout(() => {
        handleCloseDealModal();
      }, 1500);
    } catch (error) {
      setError('Operation failed. Please try again.');
      console.error('Deal operation error:', error);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (window.confirm('Are you sure you want to delete this deal?')) {
      try {
        await bestDealService.deleteDeal(dealId);
        loadData();
      } catch (error) {
        setError('Failed to delete deal');
        console.error('Delete deal error:', error);
      }
    }
  };

  // Blog functions
  const handleShowBlogModal = (blog = null) => {
    if (blog) {
      setEditingBlog(blog);
      setBlogFormData({
        title: blog.title || '',
        summary: blog.summary || '',
        content: blog.content || '',
        excerpt: blog.excerpt || '',
        published: blog.published !== false,
        premiumOnly: blog.premiumOnly || false
      });
    } else {
      setEditingBlog(null);
      setBlogFormData({
        title: '',
        summary: '',
        content: '',
        excerpt: '',
        published: false,
        premiumOnly: false
      });
    }
    setShowBlogModal(true);
  };

  const handleCloseBlogModal = () => {
    setShowBlogModal(false);
    setEditingBlog(null);
    setError('');
    setSuccess('');
  };

  const handleBlogChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setBlogFormData({ ...blogFormData, [name]: checked });
    } else {
      setBlogFormData({ ...blogFormData, [name]: value });
    }
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      
      if (editingBlog) {
        await blogService.updatePost(editingBlog.id, blogFormData);
        setSuccess('Blog post updated successfully!');
      } else {
        await blogService.createPost(blogFormData);
        setSuccess('Blog post created successfully!');
      }
      
      loadData();
      setTimeout(() => {
        handleCloseBlogModal();
      }, 1500);
    } catch (error) {
      setError('Operation failed. Please try again.');
      console.error('Blog operation error:', error);
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await blogService.deletePost(blogId);
        loadData();
      } catch (error) {
        setError('Failed to delete blog post');
        console.error('Delete blog error:', error);
      }
    }
  };

  // Update functions
  const handleShowUpdateModal = (update = null) => {
    if (update) {
      setEditingUpdate(update);
      setUpdateFormData({
        title: update.title || '',
        content: update.content || '',
        summary: update.summary || '',
        updateType: update.updateType || 'GENERAL',
        priority: update.priority || 'MEDIUM',
        published: update.published !== false,
        publishDate: update.publishDate ? new Date(update.publishDate).toISOString().slice(0, 16) : ''
      });
    } else {
      setEditingUpdate(null);
      setUpdateFormData({
        title: '',
        content: '',
        summary: '',
        updateType: 'GENERAL',
        priority: 'MEDIUM',
        published: true,
        publishDate: ''
      });
    }
    setShowUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
    setEditingUpdate(null);
    setError('');
    setSuccess('');
  };

  const handleUpdateChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setUpdateFormData({ ...updateFormData, [name]: checked });
    } else {
      setUpdateFormData({ ...updateFormData, [name]: value });
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      
      const submitData = { ...updateFormData };
      if (submitData.publishDate) {
        submitData.publishDate = new Date(submitData.publishDate).toISOString();
      }
      
      if (editingUpdate) {
        await updateService.updateUpdate(editingUpdate.id, submitData);
        setSuccess('Update updated successfully!');
      } else {
        await updateService.createUpdate(submitData);
        setSuccess('Update created successfully!');
      }
      
      loadData();
      setTimeout(() => {
        handleCloseUpdateModal();
      }, 1500);
    } catch (error) {
      setError('Operation failed. Please try again.');
      console.error('Update operation error:', error);
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    if (window.confirm('Are you sure you want to delete this update?')) {
      try {
        await updateService.deleteUpdate(updateId);
        loadData();
      } catch (error) {
        setError('Failed to delete update');
        console.error('Delete update error:', error);
      }
    }
  };

  // OneStop functions
  const handleShowOneStopModal = (node = null) => {
    if (node) {
      setEditingOneStop(node);
      setOneStopFormData({
        title: node.title || '',
        description: node.description || '',
        icon: node.icon || '',
        url: node.url || '',
        displayOrder: node.displayOrder || 0,
        active: node.active !== false,
        expanded: node.expanded || false,
        parentId: node.parent ? node.parent.id : ''
      });
    } else {
      setEditingOneStop(null);
      setOneStopFormData({
        title: '',
        description: '',
        icon: '',
        url: '',
        displayOrder: 0,
        active: true,
        expanded: false,
        parentId: ''
      });
    }
    setShowOneStopModal(true);
  };

  const handleCloseOneStopModal = () => {
    setShowOneStopModal(false);
    setEditingOneStop(null);
    setError('');
    setSuccess('');
  };

  const handleOneStopChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setOneStopFormData({ ...oneStopFormData, [name]: checked });
    } else if (name === 'displayOrder') {
      setOneStopFormData({ ...oneStopFormData, [name]: value ? parseInt(value) : 0 });
    } else {
      setOneStopFormData({ ...oneStopFormData, [name]: value });
    }
  };

  const handleOneStopSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      
      if (editingOneStop) {
        await oneStopService.updateNode(editingOneStop.id, oneStopFormData);
        setSuccess('OneStop node updated successfully!');
      } else {
        await oneStopService.createNode(oneStopFormData);
        setSuccess('OneStop node created successfully!');
      }
      
      loadData();
      setTimeout(() => {
        handleCloseOneStopModal();
      }, 1500);
    } catch (error) {
      setError('Operation failed. Please try again.');
      console.error('OneStop operation error:', error);
    }
  };

  const handleDeleteOneStop = async (nodeId) => {
    if (window.confirm('Are you sure you want to delete this OneStop node?')) {
      try {
        await oneStopService.deleteNode(nodeId);
        loadData();
      } catch (error) {
        setError('Failed to delete OneStop node');
        console.error('Delete OneStop error:', error);
      }
    }
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h2 className="mt-3">Loading Admin Dashboard...</h2>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row>
        <Col>
          <h1 className="mb-4">Admin Dashboard</h1>
          {error && <Alert variant="danger">{error}</Alert>}
        </Col>
      </Row>

      <Tabs defaultActiveKey="overview" className="mb-3">
        <Tab eventKey="overview" title="Overview">
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h3 className="text-primary">{stats.totalUsers || users.length || 0}</h3>
                  <p>Total Users</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h3 className="text-danger">{stats.adminUsers || users.filter(u => u.roles?.includes('ADMIN')).length || 0}</h3>
                  <p>Admin Users</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h3 className="text-success">{stats.enabledUsers || users.filter(u => u.enabled !== false).length || 0}</h3>
                  <p>Active Users</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body>
                  <h3 className="text-warning">{stats.disabledUsers || users.filter(u => u.enabled === false).length || 0}</h3>
                  <p>Disabled Users</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {/* Analytics Quick Access */}
          <Row className="mb-4">
            <Col>
              <Card className="border-primary">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-1">
                        <i className="fas fa-chart-line me-2 text-primary"></i>
                        Analytics Dashboard
                      </h5>
                      <p className="mb-0 text-muted">
                        View comprehensive analytics, user engagement, and performance metrics
                      </p>
                    </div>
                    <Button 
                      variant="primary" 
                      href="/api/analytics"
                      className="btn-lg"
                    >
                      <i className="fas fa-chart-bar me-2"></i>
                      View Analytics
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="users" title="User Management">
          <Row className="mb-3">
            <Col>
              <Button variant="primary" onClick={() => handleShowModal()}>
                Create New User
              </Button>
            </Col>
          </Row>

          <Card>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Roles</th>
                    <th>Status</th>
                    <th>Premium</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}</td>
                      <td>
                        {user.roles.map(role => (
                          <Badge 
                            key={role} 
                            bg={getRoleBadgeVariant(role)} 
                            className="me-1"
                          >
                            {role}
                          </Badge>
                        ))}
                      </td>
                      <td>
                        <Badge bg={user.enabled !== false ? 'success' : 'secondary'}>
                          {user.enabled !== false ? 'Active' : 'Disabled'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={user.premium ? 'warning' : 'secondary'}>
                          {user.premium ? 'Premium' : 'Regular'}
                        </Badge>
                        {user.premium && user.premiumExpiresAt && (
                          <small className="text-muted d-block">
                            Expires: {new Date(user.premiumExpiresAt).toLocaleDateString()}
                          </small>
                        )}
                      </td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          className="me-1"
                          onClick={() => handleShowModal(user)}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-warning" 
                          className="me-1"
                          onClick={() => handleToggleStatus(user.id)}
                        >
                          {user.enabled !== false ? 'Disable' : 'Enable'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-danger"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="duplicates" title="Duplicate Users">
          <Row className="mb-3">
            <Col>
              <Button variant="warning" onClick={loadDuplicateUsers} disabled={duplicateLoading}>
                {duplicateLoading ? 'Loading...' : 'Scan for Duplicates'}
              </Button>
            </Col>
          </Row>

          <Card>
            <Card.Body>
              <h5>Duplicate User Detection</h5>
              {Object.keys(duplicateUsers).length === 0 ? (
                <Alert variant="success">
                  <strong>No duplicate users found!</strong> All users have unique first name and last name combinations.
                </Alert>
              ) : (
                <>
                  <Alert variant="warning">
                    <strong>Found {Object.keys(duplicateUsers).length} duplicate name combinations.</strong> 
                    Review the users below and consider merging or removing duplicates.
                  </Alert>
                  
                  {Object.entries(duplicateUsers).map(([nameKey, users]) => (
                    <Card key={nameKey} className="mb-3">
                      <Card.Header>
                        <strong>Name: {nameKey}</strong> ({users.length} users)
                      </Card.Header>
                      <Card.Body>
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Username</th>
                              <th>Email</th>
                              <th>Created</th>
                              <th>OAuth Provider</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user) => (
                              <tr key={user.id}>
                                <td>{user.id}</td>
                                <td>{user.username}</td>
                                <td>{user.email}</td>
                                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                                <td>
                                  {user.oauthProvider ? (
                                    <Badge bg="info">{user.oauthProvider}</Badge>
                                  ) : (
                                    <Badge bg="secondary">Manual</Badge>
                                  )}
                                </td>
                                <td>
                                  <Badge bg={user.enabled ? 'success' : 'danger'}>
                                    {user.enabled ? 'Active' : 'Disabled'}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleShowModal(user)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteUser(user.id)}
                                  >
                                    Delete
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  ))}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="deals" title="Best Deals Management">
          <Row className="mb-3">
            <Col>
              <Button variant="success" onClick={() => handleShowDealModal()}>
                Create New Deal
              </Button>
            </Col>
          </Row>

          <Card>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Price</th>
                    <th>Original Price</th>
                    <th>Discount %</th>
                    <th>Status</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map(deal => (
                    <tr key={deal.id}>
                      <td>{deal.id}</td>
                      <td>
                        <div>
                          <strong>{deal.title}</strong>
                          {deal.description && (
                            <small className="text-muted d-block">
                              {deal.description.length > 50 
                                ? deal.description.substring(0, 50) + '...' 
                                : deal.description}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>{deal.price || '-'}</td>
                      <td>{deal.originalPrice || '-'}</td>
                      <td>{deal.discountPercentage ? `${deal.discountPercentage}%` : '-'}</td>
                      <td>
                        <Badge bg={deal.active ? 'success' : 'secondary'}>
                          {deal.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>{deal.displayOrder}</td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          className="me-1"
                          onClick={() => handleShowDealModal(deal)}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-danger"
                          onClick={() => handleDeleteDeal(deal.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {deals.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center text-muted">
                        No deals found. Create your first deal!
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="blogs" title="Blog Management">
          <Row className="mb-3">
            <Col>
              <Button variant="info" onClick={() => handleShowBlogModal()}>
                Create New Blog Post
              </Button>
            </Col>
          </Row>

          <Card>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Summary</th>
                    <th>Status</th>
                    <th>Premium</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map(blog => (
                    <tr key={blog.id}>
                      <td>{blog.id}</td>
                      <td>
                        <div>
                          <strong>{blog.title}</strong>
                          {blog.excerpt && (
                            <small className="text-muted d-block">
                              {blog.excerpt.length > 50 
                                ? blog.excerpt.substring(0, 50) + '...' 
                                : blog.excerpt}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        {blog.summary && blog.summary.length > 50 
                          ? blog.summary.substring(0, 50) + '...' 
                          : blog.summary || '-'}
                      </td>
                      <td>
                        <Badge bg={blog.published ? 'success' : 'secondary'}>
                          {blog.published ? 'Published' : 'Draft'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={blog.premiumOnly ? 'warning' : 'light'}>
                          {blog.premiumOnly ? 'Premium' : 'Free'}
                        </Badge>
                      </td>
                      <td>
                        {blog.createdAt ? new Date(blog.createdAt).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          className="me-1"
                          onClick={() => handleShowBlogModal(blog)}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-danger"
                          onClick={() => handleDeleteBlog(blog.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {blogs.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        No blog posts found. Create your first post!
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="updates" title="Updates Management">
          <Row className="mb-3">
            <Col>
              <Button variant="warning" onClick={() => handleShowUpdateModal()}>
                Create New Update
              </Button>
            </Col>
          </Row>

          <Card>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Publish Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {updates.map(update => (
                    <tr key={update.id}>
                      <td>{update.id}</td>
                      <td>
                        <div>
                          <strong>{update.title}</strong>
                          {update.summary && (
                            <small className="text-muted d-block">
                              {update.summary.length > 50 
                                ? update.summary.substring(0, 50) + '...' 
                                : update.summary}
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge bg="info">{update.updateType}</Badge>
                      </td>
                      <td>
                        <Badge bg={
                          update.priority === 'CRITICAL' ? 'danger' :
                          update.priority === 'HIGH' ? 'warning' :
                          update.priority === 'MEDIUM' ? 'primary' : 'secondary'
                        }>
                          {update.priority}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={update.published ? 'success' : 'secondary'}>
                          {update.published ? 'Published' : 'Draft'}
                        </Badge>
                      </td>
                      <td>
                        {update.publishDate ? new Date(update.publishDate).toLocaleDateString() : '-'}
                      </td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          className="me-1"
                          onClick={() => handleShowUpdateModal(update)}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-danger"
                          onClick={() => handleDeleteUpdate(update.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {updates.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        No updates found. Create your first update!
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="surveys" title="Survey Management">
          <SurveyManager />
        </Tab>

        <Tab eventKey="onestop" title="OneStop Management">
          <Row className="mb-3">
            <Col>
              <Button variant="dark" onClick={() => handleShowOneStopModal()}>
                Create New OneStop Node
              </Button>
            </Col>
          </Row>

          <Card>
            <Card.Body>
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>URL</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {oneStopNodes.map(node => (
                    <tr key={node.id}>
                      <td>{node.id}</td>
                      <td>
                        <div>
                          {node.icon && <i className={`${node.icon} me-2`}></i>}
                          <strong>{node.title}</strong>
                        </div>
                      </td>
                      <td>
                        {node.description && node.description.length > 50 
                          ? node.description.substring(0, 50) + '...' 
                          : node.description || '-'}
                      </td>
                      <td>
                        {node.url ? (
                          <a href={node.url} target="_blank" rel="noopener noreferrer" className="text-truncate d-block" style={{maxWidth: '150px'}}>
                            {node.url}
                          </a>
                        ) : '-'}
                      </td>
                      <td>{node.displayOrder}</td>
                      <td>
                        <Badge bg={node.active ? 'success' : 'secondary'}>
                          {node.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          className="me-1"
                          onClick={() => handleShowOneStopModal(node)}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline-danger"
                          onClick={() => handleDeleteOneStop(node.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {oneStopNodes.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        No OneStop nodes found. Create your first node!
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* User Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingUser ? 'Edit User' : 'Create New User'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {success && <Alert variant="success">{success}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username *</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Password {editingUser ? '(leave blank to keep current)' : '*'}</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!editingUser}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Roles</Form.Label>
              <Form.Select
                name="roles"
                multiple
                value={formData.roles}
                onChange={handleChange}
              >
                <option value="USER">USER</option>
                <option value="MODERATOR">MODERATOR</option>
                <option value="ADMIN">ADMIN</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Hold Ctrl/Cmd to select multiple roles
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="enabled"
                label="Account Enabled"
                checked={formData.enabled}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="premium"
                label="Premium User"
                checked={formData.premium}
                onChange={handleChange}
              />
            </Form.Group>

            {formData.premium && (
              <Form.Group className="mb-3">
                <Form.Label>Premium Expires At (Optional)</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="premiumExpiresAt"
                  value={formData.premiumExpiresAt ? new Date(formData.premiumExpiresAt).toISOString().slice(0, 16) : ''}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">
                  Leave empty for lifetime premium
                </Form.Text>
              </Form.Group>
            )}

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Deal Modal */}
      <Modal show={showDealModal} onHide={handleCloseDealModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingDeal ? 'Edit Deal' : 'Create New Deal'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {success && <Alert variant="success">{success}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={handleDealSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={dealFormData.title}
                onChange={handleDealChange}
                required
                placeholder="e.g., 50% Off Premium Laptops"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={dealFormData.description}
                onChange={handleDealChange}
                placeholder="Detailed description of the deal..."
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Current Price</Form.Label>
                  <Form.Control
                    type="text"
                    name="price"
                    value={dealFormData.price}
                    onChange={handleDealChange}
                    placeholder="e.g., $299, ₹15,999, Free"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Original Price</Form.Label>
                  <Form.Control
                    type="text"
                    name="originalPrice"
                    value={dealFormData.originalPrice}
                    onChange={handleDealChange}
                    placeholder="e.g., $599, ₹25,999"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Discount Percentage</Form.Label>
                  <Form.Control
                    type="number"
                    name="discountPercentage"
                    value={dealFormData.discountPercentage}
                    onChange={handleDealChange}
                    min="0"
                    max="100"
                    placeholder="e.g., 50"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="displayOrder"
                    value={dealFormData.displayOrder}
                    onChange={handleDealChange}
                    min="0"
                    placeholder="0 = first, 1 = second, etc."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Image URL</Form.Label>
              <Form.Control
                type="url"
                name="imageUrl"
                value={dealFormData.imageUrl}
                onChange={handleDealChange}
                placeholder="https://example.com/product-image.jpg"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Link URL</Form.Label>
              <Form.Control
                type="url"
                name="linkUrl"
                value={dealFormData.linkUrl}
                onChange={handleDealChange}
                placeholder="https://example.com/buy-product"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="active"
                label="Active (visible on website)"
                checked={dealFormData.active}
                onChange={handleDealChange}
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseDealModal}>
                Cancel
              </Button>
              <Button variant="success" type="submit">
                {editingDeal ? 'Update Deal' : 'Create Deal'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Blog Modal */}
      <Modal show={showBlogModal} onHide={handleCloseBlogModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {success && <Alert variant="success">{success}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={handleBlogSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={blogFormData.title}
                onChange={handleBlogChange}
                required
                placeholder="Enter blog post title"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Summary *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="summary"
                value={blogFormData.summary}
                onChange={handleBlogChange}
                required
                placeholder="Brief summary of the blog post"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Excerpt</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="excerpt"
                value={blogFormData.excerpt}
                onChange={handleBlogChange}
                placeholder="Short excerpt for previews"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content *</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                name="content"
                value={blogFormData.content}
                onChange={handleBlogChange}
                required
                placeholder="Full blog post content (supports HTML)"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="published"
                    label="Published (visible to users)"
                    checked={blogFormData.published}
                    onChange={handleBlogChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="premiumOnly"
                    label="Premium Only"
                    checked={blogFormData.premiumOnly}
                    onChange={handleBlogChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseBlogModal}>
                Cancel
              </Button>
              <Button variant="info" type="submit">
                {editingBlog ? 'Update Post' : 'Create Post'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Update Modal */}
      <Modal show={showUpdateModal} onHide={handleCloseUpdateModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingUpdate ? 'Edit Update' : 'Create New Update'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {success && <Alert variant="success">{success}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={handleUpdateSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={updateFormData.title}
                onChange={handleUpdateChange}
                required
                placeholder="Update title"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Summary</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="summary"
                value={updateFormData.summary}
                onChange={handleUpdateChange}
                placeholder="Brief summary of the update"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content *</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                name="content"
                value={updateFormData.content}
                onChange={handleUpdateChange}
                required
                placeholder="Detailed update content"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    name="updateType"
                    value={updateFormData.updateType}
                    onChange={handleUpdateChange}
                  >
                    <option value="GENERAL">General</option>
                    <option value="FEATURE">Feature</option>
                    <option value="BUGFIX">Bug Fix</option>
                    <option value="MAINTENANCE">Maintenance</option>
                    <option value="ANNOUNCEMENT">Announcement</option>
                    <option value="SECURITY">Security</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="priority"
                    value={updateFormData.priority}
                    onChange={handleUpdateChange}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Publish Date</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="publishDate"
                    value={updateFormData.publishDate}
                    onChange={handleUpdateChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="published"
                label="Published (visible to users)"
                checked={updateFormData.published}
                onChange={handleUpdateChange}
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseUpdateModal}>
                Cancel
              </Button>
              <Button variant="warning" type="submit">
                {editingUpdate ? 'Update' : 'Create Update'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* OneStop Modal */}
      <Modal show={showOneStopModal} onHide={handleCloseOneStopModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingOneStop ? 'Edit OneStop Node' : 'Create New OneStop Node'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {success && <Alert variant="success">{success}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={handleOneStopSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={oneStopFormData.title}
                onChange={handleOneStopChange}
                required
                placeholder="Node title"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={oneStopFormData.description}
                onChange={handleOneStopChange}
                placeholder="Node description"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Icon (CSS class)</Form.Label>
                  <Form.Control
                    type="text"
                    name="icon"
                    value={oneStopFormData.icon}
                    onChange={handleOneStopChange}
                    placeholder="e.g., fas fa-home, bi bi-house"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="displayOrder"
                    value={oneStopFormData.displayOrder}
                    onChange={handleOneStopChange}
                    min="0"
                    placeholder="0"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>URL</Form.Label>
              <Form.Control
                type="url"
                name="url"
                value={oneStopFormData.url}
                onChange={handleOneStopChange}
                placeholder="https://example.com"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Parent Node</Form.Label>
              <Form.Select
                name="parentId"
                value={oneStopFormData.parentId}
                onChange={handleOneStopChange}
              >
                <option value="">No Parent (Root Node)</option>
                {oneStopNodes.map(node => (
                  <option key={node.id} value={node.id}>
                    {node.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="active"
                    label="Active (visible to users)"
                    checked={oneStopFormData.active}
                    onChange={handleOneStopChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="expanded"
                    label="Expanded by default"
                    checked={oneStopFormData.expanded}
                    onChange={handleOneStopChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={handleCloseOneStopModal}>
                Cancel
              </Button>
              <Button variant="dark" type="submit">
                {editingOneStop ? 'Update Node' : 'Create Node'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminDashboard;